import { Match, Option, Predicate } from 'effect';
import * as EffectRegExp from 'effect/RegExp';

import {
  canonicalTokens,
  decodeFilterCode,
  decodeFilterValues,
  decodeSortToken,
  type FilterCode,
  type FilterToken,
  type SortToken,
} from './filterTokens';
import type { ListingUrlSyntax, ParsedTokens, ReadHints } from './ListingUrlSyntax';
import type { ListingUrl, QueryPair } from './listingUrl';

export type QuerySyntaxOptions = {
  readonly filterPrefix: string;
  readonly rangeMinSuffix: string;
  readonly rangeMaxSuffix: string;
  readonly valueSeparator: string;
  readonly pageParam: string;
  readonly sortParam: string;
  readonly preserve: ReadonlyArray<string>;
};

const parsedNumber = (value: string): number | undefined => {
  // `Number('')` is 0, so an empty bound would silently become a zero bound.
  if (value.trim() === '') return undefined;
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
};

const isTerms = (filter: FilterToken): filter is Extract<FilterToken, { readonly _tag: 'Terms' }> =>
  Predicate.isTagged('Terms')(filter);

type TermFilter = Extract<FilterToken, { readonly _tag: 'Terms' }>;

const isRange = (filter: FilterToken): filter is Extract<FilterToken, { readonly _tag: 'Range' }> =>
  Predicate.isTagged('Range')(filter);

const addTerm = (filters: FilterToken[], code: FilterCode, values: TermFilter['values']): void => {
  const isTarget = (filter: FilterToken): filter is TermFilter =>
    isTerms(filter) && filter.code === code;
  const target = filters.find(isTarget);

  if (target === undefined) {
    filters.push({ _tag: 'Terms', code, values });
    return;
  }

  filters[filters.indexOf(target)] = { ...target, values: [...target.values, ...values] };
};

const addRange = (
  filters: FilterToken[],
  code: FilterCode,
  edge: 'min' | 'max',
  value: number
): void => {
  const target = filters.find((filter) => filter.code === code && isRange(filter));
  if (target === undefined) {
    filters.push({
      _tag: 'Range',
      code,
      min: edge === 'min' ? value : null,
      max: edge === 'max' ? value : null,
    });
    return;
  }

  filters[filters.indexOf(target)] = { ...target, [edge]: value };
};

/** Creates the query-parameter listing URL strategy. */
export const querySyntax = (options: QuerySyntaxOptions): ListingUrlSyntax => {
  const valueAlphabet = /^[a-z0-9.-]+$/;
  const codeAlphabet = /^[a-z][a-z0-9-]*$/;
  const reservedNames = [options.pageParam, options.sortParam, ...options.preserve];

  // A written filter value must need no URL encoding. A separator that a value
  // token can hold would split one value into two on a read.
  if (
    options.valueSeparator === '' ||
    encodeURIComponent(options.valueSeparator) !== options.valueSeparator ||
    [...options.valueSeparator].some((character) => valueAlphabet.test(character))
  ) {
    throw new Error(
      `The value separator "${options.valueSeparator}" needs URL encoding or fits a value token.`
    );
  }

  const read = (url: ListingUrl, _hints: ReadHints): ParsedTokens => {
    const filters: FilterToken[] = [];
    const preserved: QueryPair[] = [];
    const dropped: string[] = [];
    let page = 1;
    let sort: SortToken | null = null;

    for (const [name, value] of url.query) {
      if (options.preserve.includes(name)) {
        preserved.push([name, value]);
        continue;
      }
      if (name === options.pageParam) {
        const candidate = Number(value);
        if (Number.isInteger(candidate) && candidate >= 1) page = candidate;
        else dropped.push(`${name}=${value}`);
        continue;
      }
      if (name === options.sortParam) {
        const token = decodeSortToken(value);
        if (Option.isSome(token)) sort = token.value;
        else dropped.push(`${name}=${value}`);
        continue;
      }
      if (!name.startsWith(options.filterPrefix)) {
        dropped.push(`${name}=${value}`);
        continue;
      }

      const raw = name.slice(options.filterPrefix.length);
      const minimum = raw.endsWith(options.rangeMinSuffix);
      const maximum = raw.endsWith(options.rangeMaxSuffix);
      const code = decodeFilterCode(
        minimum
          ? raw.slice(0, -options.rangeMinSuffix.length)
          : maximum
            ? raw.slice(0, -options.rangeMaxSuffix.length)
            : raw
      );

      if (Option.isNone(code)) {
        dropped.push(`${name}=${value}`);
        continue;
      }
      if (minimum || maximum) {
        const number = parsedNumber(value);
        if (number === undefined) dropped.push(`${name}=${value}`);
        else addRange(filters, code.value, minimum ? 'min' : 'max', number);
        continue;
      }

      const values = decodeFilterValues(value.split(options.valueSeparator));
      if (Option.isSome(values)) addTerm(filters, code.value, values.value);
      else dropped.push(`${name}=${value}`);
    }

    return {
      base: { pathname: url.pathname, query: [] },
      tokens: canonicalTokens({ filters, sort, page }),
      dropped,
      preserved,
    };
  };

  const canRepresent = (token: FilterToken): boolean =>
    codeAlphabet.test(token.code) &&
    Match.value(token).pipe(
      Match.tag('Terms', ({ values }) => values.every((value) => valueAlphabet.test(value))),
      Match.tag(
        'Range',
        ({ min, max }) =>
          (min === null || Number.isFinite(min)) && (max === null || Number.isFinite(max))
      ),
      Match.exhaustive
    );

  return {
    name: 'query',
    valueAlphabet,
    codeAlphabet,
    reservedNames,
    canRepresent,
    read,
    write: (base, tokens, preserved) => {
      const query: QueryPair[] = [];
      for (const name of options.preserve) {
        for (const pair of preserved) if (pair[0] === name) query.push(pair);
      }

      for (const token of canonicalTokens(tokens).filters) {
        if (!canRepresent(token)) continue;
        Match.value(token).pipe(
          Match.tag('Terms', ({ code, values }) =>
            query.push([`${options.filterPrefix}${code}`, values.join(options.valueSeparator)])
          ),
          Match.tag('Range', ({ code, min, max }) => {
            if (min !== null)
              query.push([`${options.filterPrefix}${code}${options.rangeMinSuffix}`, String(min)]);
            if (max !== null)
              query.push([`${options.filterPrefix}${code}${options.rangeMaxSuffix}`, String(max)]);
          }),
          Match.exhaustive
        );
      }

      const normalized = canonicalTokens(tokens);
      if (normalized.sort !== null) query.push([options.sortParam, normalized.sort]);
      if (normalized.page > 1) query.push([options.pageParam, String(normalized.page)]);
      return { pathname: base.pathname, query };
    },
    split: (pathname) => ({ base: pathname, rest: '' }),
    filteredPattern: new RegExp(`[?&]${EffectRegExp.escape(options.filterPrefix)}[a-z]`),
  };
};
